import { useState } from 'react';
import { Button } from '../../components/Button.jsx';
import { Icon } from '../../components/Icon.jsx';
import { Sheet } from '../../components/Sheet.jsx';
import { getGuestName, setGuestName } from '../../lib/identity.js';
import { color, font, radius, overline } from '../../theme/tokens.js';

/**
 * Shown when the app is opened via a group invite link (`/?group=CRAVE-7KQ2MX`),
 * which is what scanning the QR code does.
 *
 * Two versions of the same question. An invite to a group started from scratch
 * asks for a name, because nobody knows who is coming. An invite to a group
 * that has eaten together before already carries their names, so it asks which
 * one you are — and that answer does something typing a name cannot: it joins
 * under the member key the host knows you by, so tonight is filed against the
 * same group as last time rather than a brand new one.
 *
 * @param {object} props
 * @param {string} props.code
 * @param {import('./guestList.js').Guest[]} props.guests  Invited people nobody has claimed yet.
 * @param {(name: string, as?: string) => Promise<void>} props.onJoin  Rejects if the code is stale.
 * @param {() => void} props.onDecline
 */
export function GroupJoinPrompt({ code, guests, onJoin, onDecline }) {
  const [name, setName] = useState(getGuestName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Someone the host did not expect, on an invite that expected people. They
  // are forwarded the link often enough that the door has to stay open.
  const [otherwise, setOtherwise] = useState(false);

  const claiming = guests.length > 0 && !otherwise;

  const join = async (displayName, as) => {
    setBusy(true);
    setError('');
    try {
      const remembered = setGuestName(displayName);
      await onJoin(remembered || 'Guest', as);
    } catch (joinError) {
      // The link pointed at a group that no longer exists — say so rather than
      // silently opening an empty one.
      setError(
        joinError?.reason === 'expired'
          ? 'This group has already ended.'
          : 'This invite is no longer valid.'
      );
      setBusy(false);
    }
  };

  return (
    <Sheet onDismiss={onDecline} zIndex={112}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: color.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="users" size={18} stroke={color.inkInverse} strokeWidth={2} />
        </div>
        <div>
          <p style={{ ...overline, marginBottom: 2 }}>Group invite</p>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: font.weight.heavy, color: color.ink }}>
            {guests.length > 0 ? `${listNames(guests)} are eating` : `Join ${code}?`}
          </h2>
        </div>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.55, color: color.inkMuted }}>
        {claiming
          ? 'Pick your name so your votes land under it, and the group remembers you next time.'
          : "Swipe right on anywhere you'd happily eat. Your votes show up on everyone else's leaderboard instantly."}
      </p>

      {error && (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: color.primary, textAlign: 'center' }}>
          {error}
        </p>
      )}

      {claiming ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {guests.map((guest) => (
              <button
                key={guest.id}
                type="button"
                disabled={busy}
                onClick={() => join(guest.name, guest.id)}
                style={styles.guest}
              >
                <span style={styles.guestIcon}>
                  <Icon name="user" size={13} stroke={color.primary} />
                </span>
                <span style={styles.guestName}>{guest.name}</span>
                <Icon name="chevronRight" size={14} stroke={color.inkFaint} />
              </button>
            ))}
          </div>

          <Button
            variant="secondary"
            onClick={() => setOtherwise(true)}
            style={{ padding: '12px 20px', fontSize: 14, marginBottom: 10 }}
          >
            I&apos;m someone else
          </Button>
        </>
      ) : (
        <>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Your name"
            maxLength={24}
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              marginBottom: 12,
              borderRadius: radius.md,
              border: `1.5px solid ${color.border}`,
              background: color.surfaceAlt,
              fontSize: 14,
              color: color.ink,
              outline: 'none',
            }}
          />

          <Button
            onClick={() => join(name.trim() || 'Guest')}
            loading={busy}
            style={{ marginBottom: 10 }}
          >
            Join group
          </Button>
        </>
      )}

      <Button
        variant="secondary"
        onClick={onDecline}
        style={{ padding: '12px 20px', fontSize: 14 }}
      >
        Swipe on my own
      </Button>
    </Sheet>
  );
}

/** "Sam and Alex", "Sam, Alex and Jo", "Sam, Alex + 2" — kept to one line. */
function listNames(guests) {
  const names = guests.map((guest) => guest.name);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} + ${names.length - 2}`;
}

const styles = {
  guest: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '11px 12px',
    borderRadius: radius.md,
    border: `1px solid ${color.border}`,
    background: color.surfaceAlt,
    cursor: 'pointer',
    textAlign: 'left',
  },
  guestIcon: {
    width: 26,
    height: 26,
    borderRadius: radius.sm,
    background: color.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  guestName: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: font.weight.semibold,
    color: color.ink,
  },
};
